import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Sparkles, Flame, FileText, Pickaxe } from "lucide-react";
import { scrollToTarget } from "@/lib/scroll";
import WalletButton from "@/components/WalletButton";

export const LOGO_URL = "/logo.jpeg";

export function Logo({ className = "h-9 w-24", testid = "nav-logo" }) {
  return (
    <span className={`inline-flex items-center overflow-hidden ${className}`}>
      <img
        src={LOGO_URL}
        alt="B&W Pay"
        className="h-full w-full object-contain invert contrast-125"
        data-testid={testid}
      />
    </span>
  );
}

const LINKS = [
  { id: "features", label: "Features" },
  { id: "how-it-works", label: "How it works" },
  { id: "demo", label: "Demo" },
  { id: "pricing", label: "Pricing" },
  { id: "roadmap", label: "Roadmap" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (pathname !== "/") navigate("/#" + id);
    else scrollToTarget("#" + id);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-line bg-ink/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
        <button
          onClick={() => {
            setOpen(false);
            if (pathname !== "/") navigate("/");
            else scrollToTarget(0);
          }}
          data-testid="nav-home-button"
          aria-label="B&W Pay home"
        >
          <Logo />
        </button>

        <nav className="hidden items-center gap-6 xl:gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => goSection(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="link-underline text-sm text-mut transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              navigate("/presale");
            }}
            data-testid="nav-link-presale"
            className="link-underline flex items-center gap-2 text-sm text-white"
          >
            Presale
            <span className="ping-dot inline-block h-1.5 w-1.5 rounded-full bg-sgreen" />
          </button>
          <button
            onClick={() => {
              setOpen(false);
              navigate("/airdrop");
            }}
            data-testid="nav-link-airdrop"
            className="link-underline flex items-center gap-2 text-sm text-white"
          >
            Airdrop
            <span className="ping-dot inline-block h-1.5 w-1.5 rounded-full bg-sgreen" />
          </button>
          <button
            onClick={() => {
              setOpen(false);
              navigate("/mining");
            }}
            data-testid="nav-link-mining"
            className="link-underline flex items-center gap-2 text-sm text-white"
          >
            Mining
            <span className="ping-dot inline-block h-1.5 w-1.5 rounded-full bg-sgreen" />
          </button>
          <a
            href="/BnW-Pay-Whitepaper.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-link-whitepaper"
            className="link-underline flex items-center gap-1.5 text-sm text-mut transition-colors hover:text-white"
          >
            <FileText className="h-3.5 w-3.5" />
            Whitepaper
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <WalletButton />
          <button
            onClick={() => goSection("demo")}
            data-testid="nav-cta"
            className="cta-glow hidden items-center gap-2 rounded-full bg-sgreen px-5 py-2.5 text-sm font-semibold text-black md:inline-flex"
          >
            Start Accepting USDC
            <ArrowUpRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line text-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="border-t border-line bg-ink/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => goSection(l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="rounded-lg px-2 py-3 text-left text-lg text-white/90 hover:bg-white/5"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/presale");
                }}
                data-testid="nav-mobile-link-presale"
                className="flex items-center gap-2 rounded-lg px-2 py-3 text-left text-lg text-white/90 hover:bg-white/5"
              >
                Presale <Flame className="h-4 w-4 text-sgreen" />
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/airdrop");
                }}
                data-testid="nav-mobile-link-airdrop"
                className="flex items-center gap-2 rounded-lg px-2 py-3 text-left text-lg text-white/90 hover:bg-white/5"
              >
                Airdrop <Sparkles className="h-4 w-4 text-sgreen" />
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/mining");
                }}
                data-testid="nav-mobile-link-mining"
                className="flex items-center gap-2 rounded-lg px-2 py-3 text-left text-lg text-white/90 hover:bg-white/5"
              >
                USDC Mining <Pickaxe className="h-4 w-4 text-sgreen" />
              </button>
              <a
                href="/BnW-Pay-Whitepaper.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-whitepaper"
                className="flex items-center gap-2 rounded-lg px-2 py-3 text-left text-lg text-white/90 hover:bg-white/5"
              >
                Whitepaper <FileText className="h-4 w-4 text-sgreen" />
              </a>
              <div className="mt-4 flex flex-col gap-3">
                <WalletButton variant="solid" />
                <button
                  onClick={() => goSection("demo")}
                  data-testid="nav-mobile-cta"
                  className="rounded-full border border-line px-5 py-3 text-center text-sm font-semibold text-white"
                >
                  Start Accepting USDC
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
